// Ticket detail — all runs against one ticket + GitHub Actions deploys.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import api from "../../api";
import useBssPoll from "../../hooks/useBssPoll";
import AssignPrModal from "./AssignPrModal";
import DeployModal from "./DeployModal";
import GroupDeployModal from "./GroupDeployModal";
import Icon from "./icons";
import RunLane from "./RunLane";
import Shell from "./Shell";
import {
  Avatar,
  ErrorState,
  GroupStatusBadge,
  Html,
  LoadingState,
  Markdown,
  RefreshButton,
  Spinner,
  StatusBadge,
  apiErrorMessage,
  formatDuration,
  formatMoney,
  isLive,
  outcomeColor,
  relativeTime,
  rowStatusClass
} from "./shared";

// Locked cross-repo contract panel. Server-trusted markdown/text from the
// architect; rendered through the safe markdown renderer (or the sanitized
// Html component if the API tags it as HTML).
function ContractPanel({ contract }) {
  if (!contract) return null;
  const isHtml = typeof contract === "object" && contract.kind === "html";
  const src =
    typeof contract === "string"
      ? contract
      : (contract && contract.content) || "";
  if (!src) return null;
  return (
    <div className="bss-card" style={{ padding: 0, marginBottom: 24 }}>
      <div
        className="bss-card-hd"
        style={{ display: "flex", alignItems: "center", gap: 10 }}
      >
        <Icon.FileText size={13} style={{ color: "var(--bss-boom-pink)" }} />
        <h3
          style={{
            fontSize: 13,
            color: "var(--bss-fg-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.06em"
          }}
        >
          Locked cross-repo contract
        </h3>
      </div>
      <div style={{ padding: "18px 20px 20px" }}>
        {isHtml ? <Html src={src} /> : <Markdown src={src} />}
      </div>
    </div>
  );
}

function runCardBorder(status, live) {
  if (status === "FAILED") return "rgba(255, 82, 71, 0.32)";
  if (live) return "var(--bss-boom-pink)";
  return "var(--bss-border)";
}

const CONFIDENCE_COLOR = {
  HIGH: "var(--bss-success, #4caf50)",
  MEDIUM: "var(--bss-warning, #ffa726)",
  LOW: "var(--bss-fg-muted)"
};

function formatTokens(n) {
  if (!n || n < 1000) return `${n || 0}`;
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

function TraceList({ label, items }) {
  return (
    <div className="bss-inv-trace-sec">
      <div className="bss-inv-trace-label">{label}</div>
      <ul className="bss-inv-trace-list">
        {items.map((it, i) => (
          <li key={i}>
            <code>{it}</code>
          </li>
        ))}
      </ul>
    </div>
  );
}

// "How it investigated" — the agent's actual exploration trace (tool calls,
// files opened, searches run, turns, tokens). Surfaced so a human can audit the
// WORK behind the conclusion. Collapsed by default; the at-a-glance stats live
// in the always-visible summary line.
function InvestigationTrace({ trace }) {
  if (!trace) return null;
  const toolCounts = trace.toolCounts || {};
  const totalCalls = Object.values(toolCounts).reduce(
    (a, b) => a + (b || 0),
    0
  );
  const tokens = (trace.inputTokens || 0) + (trace.outputTokens || 0);
  const files = Array.isArray(trace.filesRead) ? trace.filesRead : [];
  const searches = Array.isArray(trace.searches) ? trace.searches : [];
  const commands = Array.isArray(trace.commands) ? trace.commands : [];
  const stats = [
    trace.turns ? `${trace.turns} turns` : null,
    totalCalls ? `${totalCalls} tool calls` : null,
    tokens ? `${formatTokens(tokens)} tokens` : null
  ]
    .filter(Boolean)
    .join(" · ");
  if (
    !stats &&
    files.length === 0 &&
    searches.length === 0 &&
    commands.length === 0
  )
    return null;

  return (
    <details className="bss-inv-trace">
      <summary>
        <Icon.Activity size={12} />
        <span className="bss-inv-trace-title">How it investigated</span>
        {stats && <span className="bss-inv-trace-stat">{stats}</span>}
      </summary>
      <div className="bss-inv-trace-bd">
        {Object.keys(toolCounts).length > 0 && (
          <div className="bss-inv-trace-chips">
            {Object.entries(toolCounts).map(([k, v]) => (
              <span key={k} className="bss-inv-trace-chip">
                {k} <b>{v}</b>
              </span>
            ))}
          </div>
        )}
        {files.length > 0 && (
          <TraceList label={`Files read (${files.length})`} items={files} />
        )}
        {searches.length > 0 && (
          <TraceList label={`Searches (${searches.length})`} items={searches} />
        )}
        {commands.length > 0 && (
          <TraceList label={`Commands (${commands.length})`} items={commands} />
        )}
      </div>
    </details>
  );
}

// Independent verification — a SECOND agent re-checks each hypothesis against
// the cited code (grounded / refuted / unverifiable). Surfaced as a headline
// trust badge + a per-hypothesis verdict, so a reader sees not just the claim
// but whether it survived an adversarial re-check.
const OVERALL_META = {
  confirmed: {
    label: "Independently verified",
    color: "var(--bss-success, #4caf50)",
    icon: "Check"
  },
  partially_confirmed: {
    label: "Partially verified",
    color: "var(--bss-warning, #ffa726)",
    icon: "AlertTriangle"
  },
  refuted: {
    label: "Refuted on re-check",
    color: "var(--bss-danger, #ff5247)",
    icon: "AlertCircle"
  },
  inconclusive: {
    label: "Verification inconclusive",
    color: "var(--bss-fg-muted)",
    icon: "AlertCircle"
  }
};
const VERDICT_META = {
  grounded: { label: "grounded", color: "var(--bss-success, #4caf50)" },
  refuted: { label: "refuted", color: "var(--bss-danger, #ff5247)" },
  unverifiable: { label: "unverifiable", color: "var(--bss-fg-muted)" }
};

function checksByHypothesis(verification) {
  const map = {};
  if (verification && Array.isArray(verification.checks)) {
    verification.checks.forEach((c) => {
      if (c && c.hypothesis) map[c.hypothesis] = c;
    });
  }
  return map;
}

function VerificationBadge({ verification }) {
  if (!verification) return null;
  const meta =
    OVERALL_META[verification.overallVerdict] || OVERALL_META.inconclusive;
  const Ic = Icon[meta.icon] || Icon.AlertCircle;
  const checks = Array.isArray(verification.checks) ? verification.checks : [];
  const grounded = checks.filter((c) => c.verdict === "grounded").length;
  return (
    <div className="bss-inv-verif" style={{ borderColor: meta.color }}>
      <div className="bss-inv-verif-hd">
        <Ic size={13} style={{ color: meta.color }} />
        <span className="bss-inv-verif-title" style={{ color: meta.color }}>
          {meta.label}
        </span>
        {checks.length > 0 && (
          <span className="bss-inv-verif-count">
            {grounded}/{checks.length} grounded
          </span>
        )}
        {verification.independentConfidence && (
          <span className="bss-inv-verif-conf">
            {verification.independentConfidence.toLowerCase()} confidence · 2nd
            agent
          </span>
        )}
      </div>
      {verification.summary && (
        <p className="bss-inv-verif-summary">{verification.summary}</p>
      )}
    </div>
  );
}

function VerdictChip({ check }) {
  if (!check) return null;
  const m = VERDICT_META[check.verdict] || VERDICT_META.unverifiable;
  return (
    <div className="bss-inv-verdict">
      <span className="bss-dot" style={{ background: m.color }} />
      <span style={{ color: m.color, fontWeight: 600 }}>
        verified: {m.label}
      </span>
      {check.finding && (
        <span className="bss-inv-verdict-finding"> — {check.finding}</span>
      )}
    </div>
  );
}

// Bug Investigation panel — renders the Investigator's structured findings
// (embedded as `ticket.bugInvestigation` on the ticket-detail response). The
// `onReinvestigate` button POSTs a re-run; `inFlight` reflects local
// optimistic state while the new run is pending.
function InvestigationPanel({
  data,
  onReinvestigate,
  inFlight,
  onImplement,
  implementing
}) {
  if (!data) return null;
  const inv = data.investigation || {};
  const conf = (inv.confidence || "MEDIUM").toUpperCase();
  const failed = data.verdict === "failed";
  const list = (arr) => (Array.isArray(arr) ? arr : []);
  const verifChecks = checksByHypothesis(data.verification);
  // Only a completed investigation can be implemented (skipped_* verdicts have
  // no findings to seed the fix — the backend would 409).
  const canImplement = data.verdict === "investigated";
  // Surface the verifier's verdict NEXT TO the action when it isn't a clean
  // "confirmed", so a human sees a refuted/partial diagnosis before clicking.
  const verifVerdict = data.verification && data.verification.overallVerdict;
  const verifWarn =
    verifVerdict && verifVerdict !== "confirmed"
      ? OVERALL_META[verifVerdict] || OVERALL_META.inconclusive
      : null;

  return (
    <div className="bss-card bss-investigation" style={{ marginBottom: 24 }}>
      <div
        className="bss-card-hd"
        style={{ display: "flex", alignItems: "center", gap: 8 }}
      >
        <Icon.Search size={14} style={{ color: "var(--bss-boom-pink)" }} />
        <span style={{ fontWeight: 600 }}>Bug Investigation</span>
        {!failed && (
          <span
            className="bss-inv-conf"
            style={{ color: CONFIDENCE_COLOR[conf] || CONFIDENCE_COLOR.MEDIUM }}
          >
            <span className="bss-dot" /> {conf.toLowerCase()} confidence
          </span>
        )}
        {data.investigatedAt && (
          <span style={{ color: "var(--bss-fg-subtle)", fontSize: 12 }}>
            · {relativeTime(data.investigatedAt)}
          </span>
        )}
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 8,
            alignItems: "center"
          }}
        >
          {canImplement && verifWarn && (
            <span
              style={{ fontSize: 11, color: verifWarn.color }}
              title="The independent verifier did not fully confirm this diagnosis — review before implementing."
            >
              {verifWarn.label}
            </span>
          )}
          {canImplement && (
            <button
              type="button"
              className="bss-btn bss-btn--primary bss-btn--sm"
              onClick={onImplement}
              disabled={implementing || inFlight}
              title="Open a ready-to-review PR that implements this fix, seeded with this diagnosis"
            >
              {implementing ? (
                <>
                  <Spinner size={11} /> Starting…
                </>
              ) : (
                <>
                  <Icon.PlayCircle size={12} /> Implement this fix
                </>
              )}
            </button>
          )}
          <button
            type="button"
            className="bss-btn bss-btn--ghost bss-btn--sm"
            onClick={onReinvestigate}
            disabled={inFlight}
            title="Re-run the investigation"
          >
            {inFlight ? (
              <>
                <Spinner size={11} /> Investigating…
              </>
            ) : (
              <>
                <Icon.RotateCw size={12} /> Re-investigate
              </>
            )}
          </button>
        </div>
      </div>
      <div className="bss-card-bd bss-inv-bd">
        {failed ? (
          <p className="bss-inv-failed">
            The investigation did not complete
            {data.reason ? `: ${data.reason}` : "."}
          </p>
        ) : (
          <>
            {inv.summary && <p className="bss-inv-summary">{inv.summary}</p>}

            <VerificationBadge verification={data.verification} />
            <InvestigationTrace trace={data.trace} />

            {list(inv.rootCauseHypotheses).length > 0 && (
              <div className="bss-inv-sec">
                <div className="bss-inv-label">Root-cause hypotheses</div>
                <ol className="bss-inv-hyps">
                  {list(inv.rootCauseHypotheses).map((h, i) => (
                    <li key={i}>
                      <div className="bss-inv-hyp-head">
                        <span
                          className="bss-inv-hyp-conf"
                          style={{
                            color:
                              CONFIDENCE_COLOR[
                                (h.confidence || "").toUpperCase()
                              ] || "var(--bss-fg-muted)"
                          }}
                        >
                          {(h.confidence || "?").toUpperCase()}
                        </span>
                        <span className="bss-inv-hyp-text">{h.hypothesis}</span>
                      </div>
                      {list(h.evidence).length > 0 && (
                        <ul className="bss-inv-ev">
                          {list(h.evidence).map((e, j) => (
                            <li key={j}>
                              <code>{e}</code>
                            </li>
                          ))}
                        </ul>
                      )}
                      <VerdictChip check={verifChecks[i + 1]} />
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {list(inv.evidenceHighlights).length > 0 && (
              <InvList
                label="Evidence highlights"
                items={inv.evidenceHighlights}
                mono
              />
            )}

            {list(inv.codePathsToReview).length > 0 && (
              <div className="bss-inv-sec">
                <div className="bss-inv-label">Code to review</div>
                <ul className="bss-inv-paths">
                  {list(inv.codePathsToReview).map((c, i) => (
                    <li key={i}>
                      <code>
                        {c.file}
                        {c.lines ? `:${c.lines}` : ""}
                      </code>
                      {c.why && <span className="bss-inv-why"> — {c.why}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {list(inv.reproductionSteps).length > 0 && (
              <InvList
                label="Reproduction steps"
                items={inv.reproductionSteps}
                ordered
              />
            )}
            {list(inv.questionsForReporter).length > 0 && (
              <InvList
                label="Questions for the reporter"
                items={inv.questionsForReporter}
              />
            )}
            {list(inv.additionalDataWanted).length > 0 && (
              <InvList
                label="Data the agent wanted (not fetched)"
                items={inv.additionalDataWanted}
              />
            )}
            {list(inv.relatedPriorBugs).length > 0 && (
              <div className="bss-inv-sec">
                <div className="bss-inv-label">Related prior bugs</div>
                <ul className="bss-inv-related">
                  {list(inv.relatedPriorBugs).map((b, i) => (
                    <li key={i}>
                      <strong>{b.key}</strong>
                      {b.similarity ? ` — ${b.similarity}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function InvList({ label, items, ordered, mono }) {
  const Tag = ordered ? "ol" : "ul";
  return (
    <div className="bss-inv-sec">
      <div className="bss-inv-label">{label}</div>
      <Tag className="bss-inv-list">
        {items.map((it, i) => (
          <li key={i}>{mono ? <code>{it}</code> : it}</li>
        ))}
      </Tag>
    </div>
  );
}

export default function TicketDetailPage() {
  const { key } = useParams();
  const history = useHistory();
  const [deployOpen, setDeployOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchTicket = useCallback(
    () => api.bssTicket(key).then((res) => res.data),
    [key]
  );
  const { data: ticket, error, loading, refresh } = useBssPoll(fetchTicket);

  // Arriving straight from a just-triggered action (e.g. a release cut) the
  // run rows are created a beat later by the queue, so the first fetch 404s.
  // When navigated with ?pending=1, auto-retry on 404 for a short window
  // instead of flashing "No runs". Genuine mistyped-key 404s (no flag) still
  // fail fast.
  const location = useLocation();
  const pendingArrival =
    new URLSearchParams(location.search).get("pending") === "1";
  const [pendingRetries, setPendingRetries] = useState(0);
  const is404 = error?.response?.status === 404;
  useEffect(() => {
    if (!pendingArrival || ticket || !is404 || pendingRetries >= 12)
      return undefined;
    const t = setTimeout(() => {
      setPendingRetries((n) => n + 1);
      refresh();
    }, 1500);
    return () => clearTimeout(t);
  }, [pendingArrival, ticket, is404, pendingRetries, refresh]);

  const runs = useMemo(() => (ticket && ticket.runs) || [], [ticket]);
  const gha = useMemo(() => (ticket && ticket.ghaHistory) || [], [ticket]);
  // Multi-repo fields are additive — null/absent for legacy single-run
  // tickets, in which case we fall back to today's single-run rendering.
  const group = ticket && ticket.group;
  const lanes = useMemo(() => (ticket && ticket.lanes) || [], [ticket]);
  const hasLanes = lanes.length > 0;
  const totalSpend = useMemo(
    () => runs.reduce((s, r) => s + (r.costUsd || 0), 0),
    [runs]
  );

  // Bug Investigation — embedded on the ticket response. `reinvestigating`
  // is optimistic local state while a re-run is pending; it clears when a
  // NEW investigation lands (investigatedAt advances) or after a timeout.
  const investigation = ticket && ticket.bugInvestigation;
  const [reinvestigating, setReinvestigating] = useState(false);
  const [implementing, setImplementing] = useState(false);
  const reinvestBaselineRef = useRef(null);

  const onReinvestigate = useCallback(async () => {
    reinvestBaselineRef.current = investigation?.investigatedAt ?? null;
    setReinvestigating(true);
    try {
      await api.bssInvestigate(key);
      refresh();
    } catch (e) {
      setToast(apiErrorMessage(e, "Could not start investigation."));
      window.setTimeout(() => setToast(null), 4000);
      setReinvestigating(false);
    }
  }, [key, investigation, refresh]);

  // "Implement this fix" — kick off an implement run seeded with the diagnosis;
  // it opens a ready-to-review PR. Fire-and-forget: SS returns 202 PLANNING.
  const onImplementInvestigation = useCallback(async () => {
    setImplementing(true);
    try {
      let res = await api.bssImplementInvestigation(key, false);
      // A prior implement already opened a fix PR — confirm before duplicating.
      if (res && res.data && res.data.status === "already_implemented") {
        const prTxt = res.data.prNumber ? `#${res.data.prNumber}` : "one";
        if (
          !window.confirm(
            `A fix PR (${prTxt}) already exists for this ticket. Implement again anyway?`
          )
        ) {
          setToast(`A fix PR (${prTxt}) already exists for this ticket.`);
          window.setTimeout(() => setToast(null), 4000);
          return;
        }
        res = await api.bssImplementInvestigation(key, true); // operator confirmed
      }
      // `skipped` is a 200, not an error — don't claim a PR is coming when
      // nothing was queued (e.g. an implement/investigate run is already live).
      if (res && res.data && res.data.status === "skipped") {
        setToast(
          res.data.reason ||
            "An implement run is already in flight for this ticket."
        );
      } else {
        setToast(
          "Implementing the fix — a PR will open when the agent finishes."
        );
      }
      window.setTimeout(() => setToast(null), 4500);
      refresh();
    } catch (e) {
      setToast(apiErrorMessage(e, "Could not start the fix."));
      window.setTimeout(() => setToast(null), 4000);
    } finally {
      setImplementing(false);
    }
  }, [key, refresh]);

  // Clear the optimistic flag as soon as a NEW investigation lands
  // (investigatedAt advances — covers both a completed and a failed re-run,
  // since both upsert a fresh row).
  useEffect(() => {
    if (!reinvestigating) return;
    if (
      investigation?.investigatedAt &&
      investigation.investigatedAt !== reinvestBaselineRef.current
    ) {
      setReinvestigating(false);
    }
  }, [reinvestigating, investigation]);

  // Safety timeout — armed ONCE when the re-run starts. Kept in its own effect
  // keyed on `[reinvestigating]` only: the polling refresh above hands us a new
  // `investigation` object every 4s, so folding this into the landing effect
  // re-armed the timer on every poll and it never fired — a run that never
  // wrote a fresh row would spin forever.
  useEffect(() => {
    if (!reinvestigating) return undefined;
    const t = setTimeout(() => setReinvestigating(false), 90000);
    return () => clearTimeout(t);
  }, [reinvestigating]);

  // useBssPoll is fetch-once — poll while a re-investigation is pending so the
  // result appears without a manual reload.
  useEffect(() => {
    if (!reinvestigating) return undefined;
    const t = setInterval(() => refresh(), 4000);
    return () => clearInterval(t);
  }, [reinvestigating, refresh]);

  // Only block the whole page on the *initial* load. A post-deploy
  // refresh() flips loading=true with the ticket still present — keep the
  // page (toast + lanes) mounted and let the inline/stale indicators cover
  // the refetch (mirrors RunsPage's `error && !data` gating).
  if (loading && !ticket) {
    return (
      <Shell>
        <LoadingState label="Loading ticket…" />
      </Shell>
    );
  }
  if (error || !ticket) {
    return (
      <Shell>
        <ErrorState
          message={apiErrorMessage(error, "Ticket not found.")}
          onRetry={refresh}
        />
      </Shell>
    );
  }

  const latest = runs[0];

  return (
    <Shell>
      <div className="bss-crumb">
        <button
          type="button"
          className="bss-btn bss-btn--ghost bss-btn--sm"
          onClick={() => history.push("/bss/tickets")}
        >
          <Icon.ChevronLeft size={13} /> Back
        </button>
        <span>/</span>
        <Icon.Ticket size={13} />
        <span
          style={{
            fontFamily: "var(--bss-font-mono)",
            color: "var(--bss-fg-strong)",
            fontWeight: 500
          }}
        >
          {ticket.key}
        </span>
        <span style={{ marginLeft: "auto" }}>
          <RefreshButton onClick={refresh} loading={loading} />
        </span>
      </div>

      <div className="bss-card" style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 6,
                flexWrap: "wrap"
              }}
            >
              <span
                style={{
                  fontFamily: "var(--bss-font-mono)",
                  fontSize: 12.5,
                  color: "var(--bss-fg-muted)"
                }}
              >
                {ticket.key}
              </span>
              {ticket.issueType && (
                <span className="bss-badge bss-badge--gray">
                  <Icon.Hash size={11} /> {ticket.issueType}
                </span>
              )}
              {ticket.priority && (
                <span className="bss-badge bss-badge--amber">
                  <span className="bss-dot" />
                  {ticket.priority}
                </span>
              )}
            </div>
            <h1 style={{ fontSize: 22, marginBottom: 10 }}>{ticket.summary}</h1>
            {ticket.description && (
              <p
                style={{
                  color: "var(--bss-fg-muted)",
                  fontSize: 13.5,
                  maxWidth: 720,
                  lineHeight: 1.6
                }}
              >
                {ticket.description}
              </p>
            )}

            <div
              style={{
                display: "flex",
                gap: 28,
                marginTop: 18,
                flexWrap: "wrap",
                fontSize: 12.5
              }}
            >
              {ticket.reporter && (
                <div>
                  <div className="bss-ticket-meta-k">Reporter</div>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8
                    }}
                  >
                    <Avatar name={ticket.reporter} size="sm" />
                    <span style={{ color: "var(--bss-fg)" }}>
                      {ticket.reporter}
                    </span>
                  </span>
                </div>
              )}
              <div>
                <div className="bss-ticket-meta-k">Total runs</div>
                <span
                  style={{
                    fontSize: 13.5,
                    color: "var(--bss-fg)",
                    fontWeight: 500
                  }}
                >
                  {runs.length}
                </span>
              </div>
              <div>
                <div className="bss-ticket-meta-k">Total spend</div>
                <span
                  style={{
                    fontSize: 13.5,
                    color: "var(--bss-fg)",
                    fontWeight: 500,
                    fontFamily: "var(--bss-font-mono)"
                  }}
                >
                  {formatMoney(totalSpend)}
                </span>
              </div>
              {latest && (
                <div>
                  <div className="bss-ticket-meta-k">Latest status</div>
                  <StatusBadge
                    status={latest.status}
                    pulsing={isLive(latest.status)}
                  />
                </div>
              )}
              {ticket.branch && (
                <div>
                  <div className="bss-ticket-meta-k">Branch</div>
                  <code className="bss-code-inline" style={{ fontSize: 11.5 }}>
                    {ticket.branch}
                  </code>
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              flexShrink: 0,
              width: 220
            }}
          >
            <button
              type="button"
              className="bss-btn bss-btn--primary"
              onClick={() => setDeployOpen(true)}
            >
              <Icon.PlayCircle size={13} /> Trigger GitHub Action
            </button>
            <a
              className="bss-btn bss-btn--ghost"
              href={`/bss/tickets/${encodeURIComponent(ticket.key)}/qa`}
            >
              <Icon.TestTube size={13} /> Open QA workspace
            </a>
            <button
              type="button"
              className="bss-btn bss-btn--ghost"
              onClick={() => setAssignOpen(true)}
              title={`Assign a PR to ${ticket.key}`}
            >
              <Icon.Plus size={13} /> Assign PR…
            </button>
            {ticket.jiraUrl && (
              <a
                className="bss-btn bss-btn--ghost"
                href={ticket.jiraUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon.ExternalLink size={13} /> Open in Jira
              </a>
            )}
          </div>
        </div>
      </div>

      {group && (
        <div className="bss-card" style={{ padding: 20, marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap"
            }}
          >
            <h2 className="bss-ticket-section-h" style={{ margin: 0 }}>
              Cross-repo group
            </h2>
            <GroupStatusBadge status={group.status} />
            {group.id && (
              <button
                type="button"
                className="bss-btn bss-btn--ghost bss-btn--sm"
                style={{ marginLeft: "auto" }}
                onClick={() => history.push(`/bss/groups/${group.id}`)}
              >
                <Icon.ExternalLink size={12} /> Open group
              </button>
            )}
          </div>
          {(group.affectedRepos || []).length > 0 && (
            <div
              style={{
                marginTop: 12,
                display: "flex",
                gap: 6,
                flexWrap: "wrap"
              }}
            >
              {group.affectedRepos.map((r) => (
                <span key={r} className="bss-badge bss-badge--gray">
                  <Icon.Github size={11} /> {r}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {group && <ContractPanel contract={group.contract} />}

      {investigation && (
        <InvestigationPanel
          data={investigation}
          onReinvestigate={onReinvestigate}
          inFlight={reinvestigating}
          onImplement={onImplementInvestigation}
          implementing={implementing}
        />
      )}

      {hasLanes && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 14
            }}
          >
            <h2 className="bss-ticket-section-h">Per-repo lanes</h2>
            <span style={{ color: "var(--bss-fg-subtle)", fontSize: 12 }}>
              {lanes.length} {lanes.length === 1 ? "lane" : "lanes"}
            </span>
          </div>
          {lanes.map((lane) => (
            <RunLane
              key={lane.repoId || (lane.run && lane.run.id) || lane.githubRepo}
              repoId={lane.repoId}
              githubRepo={lane.githubRepo}
              run={lane.run}
              prUrl={lane.prUrl}
              status={lane.status}
              deployWorkflows={lane.deployWorkflows}
              relatedRuns={lane.relatedRuns}
              hideDeploy={hasLanes}
              ticketKey={ticket.key}
              onNavigateRun={(rid) => history.push(`/bss/runs/${rid}`)}
              onDeployed={(res, env, repo) => {
                setToast(
                  `Dispatched deploy → ${
                    repo && (repo.githubRepo || repo.repoId)
                  } · ${env ? env.label : "environment"}`
                );
                window.setTimeout(() => setToast(null), 3500);
                refresh();
              }}
              onForceReview={({ ok, error, repoId, prNumber }) => {
                setToast(
                  ok
                    ? `Force-review queued → ${repoId} #${prNumber}. Watch the Runs tab for the new review.`
                    : `Force-review failed for ${repoId} #${prNumber}: ${error}`
                );
                window.setTimeout(() => setToast(null), 4500);
                if (ok) refresh();
              }}
            />
          ))}
        </>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 14
        }}
      >
        <h2 className="bss-ticket-section-h">Recent deploys</h2>
        <span style={{ color: "var(--bss-fg-subtle)", fontSize: 12 }}>
          {gha.length} {gha.length === 1 ? "run" : "runs"}
        </span>
        <button
          type="button"
          className="bss-btn bss-btn--ghost bss-btn--sm"
          style={{ marginLeft: "auto" }}
          onClick={() => setDeployOpen(true)}
        >
          <Icon.PlayCircle size={12} /> New deploy
        </button>
      </div>

      <div
        className="bss-card"
        style={{ padding: 0, marginBottom: 28, overflow: "hidden" }}
      >
        {gha.length === 0 ? (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              color: "var(--bss-fg-muted)"
            }}
          >
            <Icon.Github
              size={22}
              style={{ color: "var(--bss-fg-subtle)", marginBottom: 8 }}
            />
            <div
              style={{
                fontWeight: 500,
                color: "var(--bss-fg)",
                fontSize: 13.5
              }}
            >
              No deploys yet.
            </div>
            <div
              style={{
                fontSize: 12.5,
                marginTop: 4,
                marginBottom: 12
              }}
            >
              Trigger the deploy workflow on the agent&apos;s branch to push
              this fix to an environment.
            </div>
          </div>
        ) : (
          <table className="bss-table">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Run</th>
                <th style={{ width: 130 }}>Environment</th>
                <th style={{ width: 120 }}>Status</th>
                <th>Workflow</th>
                <th style={{ width: 100 }}>By</th>
                <th style={{ width: 90, textAlign: "right" }}>Duration</th>
                <th style={{ width: 110 }}>Started</th>
                <th style={{ width: 36 }} aria-label="link" />
              </tr>
            </thead>
            <tbody>
              {gha.map((g) => {
                const dotColor = outcomeColor(g.status);
                return (
                  <tr
                    key={g.id}
                    className={rowStatusClass(
                      g.status === "failed" ? "FAILED" : g.status,
                      g.status === "running"
                    )}
                  >
                    <td>
                      <span
                        className="bss-muted-mono"
                        style={{ color: "var(--bss-fg)" }}
                      >
                        #{g.runNumber}
                      </span>
                    </td>
                    <td>
                      <code
                        className="bss-code-inline"
                        style={{ fontSize: 11.5 }}
                      >
                        {g.env}
                      </code>
                    </td>
                    <td>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          fontSize: 12.5
                        }}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            background: dotColor
                          }}
                        />
                        <span
                          style={{
                            color: "var(--bss-fg)",
                            textTransform: "capitalize"
                          }}
                        >
                          {g.status}
                        </span>
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: 12.5
                        }}
                      >
                        <Icon.Github
                          size={11}
                          style={{ color: "var(--bss-fg-muted)" }}
                        />
                        <code
                          className="bss-code-inline"
                          style={{ fontSize: 11.5 }}
                        >
                          {g.workflow}
                        </code>
                        <span style={{ color: "var(--bss-fg-subtle)" }}>·</span>
                        <code
                          className="bss-code-inline"
                          style={{ fontSize: 11 }}
                        >
                          {g.sha}
                        </code>
                      </span>
                    </td>
                    <td>
                      {g.actor ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8
                          }}
                        >
                          <Avatar name={g.actor} size="sm" />
                          <span
                            style={{
                              fontSize: 12.5,
                              color: "var(--bss-fg-muted)"
                            }}
                          >
                            {g.actor.split(" ")[0]}
                          </span>
                        </span>
                      ) : (
                        <span style={{ color: "var(--bss-fg-subtle)" }}>—</span>
                      )}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        fontFamily: "var(--bss-font-mono)",
                        fontSize: 12,
                        color: "var(--bss-fg-muted)"
                      }}
                    >
                      {g.durationSec != null
                        ? formatDuration(g.durationSec)
                        : "—"}
                    </td>
                    <td
                      style={{
                        fontSize: 12.5,
                        color: "var(--bss-fg-muted)"
                      }}
                    >
                      {relativeTime(g.startedAt)}
                    </td>
                    <td>
                      <a
                        href={g.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bss-icon-btn"
                        title="Open on GitHub"
                      >
                        <Icon.ExternalLink size={12} />
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!hasLanes && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 14
            }}
          >
            <h2 className="bss-ticket-section-h">Run history</h2>
            <span style={{ color: "var(--bss-fg-subtle)", fontSize: 12 }}>
              {runs.length} attempts
            </span>
          </div>

          <div className="bss-card" style={{ padding: 24 }}>
            {runs.length === 0 ? (
              <div style={{ color: "var(--bss-fg-muted)", fontSize: 13 }}>
                No runs recorded for this ticket.
              </div>
            ) : (
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    position: "absolute",
                    left: 11,
                    top: 8,
                    bottom: 8,
                    width: 2,
                    background: "var(--bss-border)"
                  }}
                />
                {runs.map((r, i) => {
                  const live = isLive(r.status);
                  const dotColor = outcomeColor(r.status);
                  return (
                    <div
                      key={r.id}
                      style={{
                        position: "relative",
                        paddingLeft: 36,
                        paddingBottom: i < runs.length - 1 ? 22 : 0
                      }}
                    >
                      <span
                        style={{
                          position: "absolute",
                          left: 4,
                          top: 4,
                          width: 16,
                          height: 16,
                          borderRadius: 999,
                          background: dotColor,
                          border: "2px solid var(--bss-surface)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff"
                        }}
                      >
                        {r.status === "COMPLETED" && <Icon.Check size={9} />}
                        {r.status === "FAILED" && <Icon.X size={9} />}
                        {live && <Spinner size={7} />}
                      </span>

                      <div
                        className="bss-card"
                        style={{
                          padding: 16,
                          cursor: "pointer",
                          borderColor: runCardBorder(r.status, live),
                          background:
                            r.status === "FAILED"
                              ? "rgba(255, 82, 71, 0.04)"
                              : "var(--bss-surface-soft)"
                        }}
                        onClick={() => history.push(`/bss/runs/${r.id}`)}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            marginBottom: 8,
                            flexWrap: "wrap"
                          }}
                        >
                          <StatusBadge status={r.status} pulsing={live} />
                          <span
                            style={{
                              fontSize: 12.5,
                              color: "var(--bss-fg-muted)"
                            }}
                          >
                            Attempt{" "}
                            <strong style={{ color: "var(--bss-fg)" }}>
                              {r.attempt}
                            </strong>
                          </span>
                          <span style={{ color: "var(--bss-fg-subtle)" }}>
                            ·
                          </span>
                          <span
                            style={{
                              fontSize: 12.5,
                              color: "var(--bss-fg-muted)"
                            }}
                          >
                            <Icon.Clock
                              size={11}
                              style={{ verticalAlign: -1, marginRight: 4 }}
                            />
                            {relativeTime(r.startedAt)} ·{" "}
                            {formatDuration(r.durationSec)}
                          </span>
                          <span
                            style={{
                              marginLeft: "auto",
                              display: "flex",
                              alignItems: "center",
                              gap: 10
                            }}
                          >
                            <span
                              className="bss-muted-mono"
                              style={{ color: "var(--bss-fg)" }}
                            >
                              {formatMoney(r.costUsd)}
                            </span>
                            {r.pr && (
                              <a
                                className="bss-badge bss-badge--blue"
                                href={r.pr.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Icon.GitPullRequest size={11} /> #{r.pr.number}
                              </a>
                            )}
                            <Icon.ChevronRight
                              size={13}
                              style={{ color: "var(--bss-fg-subtle)" }}
                            />
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: 12.5,
                            color: "var(--bss-fg-muted)",
                            lineHeight: 1.5
                          }}
                        >
                          Run ID{" "}
                          <span style={{ fontFamily: "var(--bss-font-mono)" }}>
                            {r.id}
                          </span>
                          {r.branch && (
                            <>
                              {" · "}
                              <span
                                className="bss-code-inline"
                                style={{ fontSize: 11 }}
                              >
                                {r.branch}
                              </span>
                            </>
                          )}
                        </div>
                        {r.status === "FAILED" && r.errorPreview && (
                          <div
                            className="bss-preview-err"
                            style={{ marginTop: 8, maxWidth: "none" }}
                          >
                            <Icon.AlertTriangle size={11} /> {r.errorPreview}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {hasLanes ? (
        <GroupDeployModal
          open={deployOpen}
          onClose={() => setDeployOpen(false)}
          groupId={group ? group.id : null}
          ticketKey={ticket.key}
          branch={ticket.branch}
          lanes={lanes}
          onDeployed={(env) => {
            setToast(`Dispatched deploy → ${env}`);
            window.setTimeout(() => setToast(null), 3500);
            refresh();
          }}
        />
      ) : (
        <DeployModal
          open={deployOpen}
          onClose={() => setDeployOpen(false)}
          ticketKey={ticket.key}
          branch={ticket.branch}
          environments={ticket.environments}
          onDeployed={(res, env) => {
            setToast(`Dispatched deploy → ${env ? env.label : "environment"}`);
            window.setTimeout(() => setToast(null), 3500);
            refresh();
          }}
        />
      )}

      <AssignPrModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        initialKey={ticket.key}
        onAssigned={refresh}
      />

      {toast && (
        <div className="bss-toast">
          <Icon.PlayCircle
            size={16}
            style={{ color: "var(--bss-boom-pink)" }}
          />
          {toast}
        </div>
      )}
    </Shell>
  );
}
